import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Card, CardContent } from "../../../components/tailwind/card";
import {
    Tooltip,
    TooltipContent,
    TooltipTrigger,
} from "../../../components/tailwind/tooltip";

const DELAY_CLASSES = [
    "animate-delay-75",
    "animate-delay-150",
    "animate-delay-200",
    "animate-delay-300",
];

const getBgColorClasses = (bgColor) => {
    const colorMap = {
        green: "bg-emerald-50 dark:bg-emerald-950/50 group-hover:bg-emerald-100 dark:group-hover:bg-emerald-950/70",
        blue: "bg-blue-50 dark:bg-blue-950/50 group-hover:bg-blue-100 dark:group-hover:bg-blue-950/70",
        purple: "bg-purple-50 dark:bg-purple-950/50 group-hover:bg-purple-100 dark:group-hover:bg-purple-950/70",
    };
    return colorMap[bgColor] || "bg-gray-50 dark:bg-slate-800";
};

const getTextColorClasses = (bgColor) => {
    const colorMap = {
        green: "text-[#0F6E56] dark:text-emerald-400",
        blue: "text-blue-600 dark:text-blue-400",
        purple: "text-purple-600 dark:text-purple-400",
    };
    return colorMap[bgColor] || "text-gray-600 dark:text-gray-400";
};

const getHoverTextClasses = (hoverText) => {
    const colorMap = {
        "green-600": "group-hover:text-[#0F6E56] dark:group-hover:text-emerald-400",
        "blue-600": "group-hover:text-blue-600 dark:group-hover:text-blue-400",
        "purple-600": "group-hover:text-purple-600 dark:group-hover:text-purple-400",
    };
    return colorMap[hoverText] || "";
};

const getHoverBorderClasses = (hoverBorder) => {
    const colorMap = {
        "green-500": "hover:border-[#0F6E56] dark:hover:border-emerald-500",
        "blue-500": "hover:border-blue-500 dark:hover:border-blue-400",
        "purple-500": "hover:border-purple-500 dark:hover:border-purple-400",
    };
    return colorMap[hoverBorder] || "";
};

export const ActionCard = ({ action, index = 0 }) => {
    const delayClass = DELAY_CLASSES[index] || "animate-delay-300";

    const CardContentComponent = (
        <CardContent className="p-5 sm:p-6 flex items-center gap-4">
            <div className={`h-12 w-12 shrink-0 rounded-xl flex items-center justify-center transition-colors ${getBgColorClasses(action.bgColor)}`}>
                <action.icon className={`h-6 w-6 ${getTextColorClasses(action.bgColor)}`} />
            </div>
            <div className="flex-1 min-w-0">
                <h4
                    className={`font-semibold ${
                        action.enabled
                            ? `text-gray-900 dark:text-white ${getHoverTextClasses(action.hoverText)}`
                            : "text-gray-400 dark:text-gray-500"
                    } transition-colors`}
                >
                    {action.title}
                </h4>
                <p className={`text-sm ${action.enabled ? "text-gray-500 dark:text-gray-400" : "text-gray-400 dark:text-gray-500"}`}>
                    {action.description}
                </p>
            </div>
            <ArrowRight
                className={`h-5 w-5 shrink-0 ${
                    action.enabled
                        ? "text-gray-300 dark:text-slate-600 group-hover:text-gray-500 dark:group-hover:text-slate-400 group-hover:translate-x-1"
                        : "text-gray-300 dark:text-slate-700"
                } transition-all`}
            />
        </CardContent>
    );

    if (action.enabled) {
        return (
            <Link to={action.link} className={`block h-full animate-fade-in-up ${delayClass}`}>
                <Card
                    className={`group h-full cursor-pointer border-gray-200 dark:border-slate-800 bg-white dark:bg-slate-900 ${getHoverBorderClasses(action.hoverBorder)} wa-card-hover`}
                >
                    {CardContentComponent}
                </Card>
            </Link>
        );
    }

    return (
        <Tooltip>
            <TooltipTrigger asChild>
                <div className={`h-full animate-fade-in-up ${delayClass}`}>
                    <Card className="h-full border-gray-200 dark:border-slate-800 bg-white dark:bg-slate-900 opacity-60 cursor-not-allowed">
                        {CardContentComponent}
                    </Card>
                </div>
            </TooltipTrigger>
            <TooltipContent>
                <p>{action.tooltip}</p>
            </TooltipContent>
        </Tooltip>
    );
};
